import { instance } from "api/apiBase";
import {
  PrescreeningCreditScore,
  PrescreeningGrossMonthlyIncome,
  PrescreeningQuestionRecord,
  QuestionType
} from "shared/types";
import { Lead } from "views/leads-id/types";

interface PrescreeningQuestionAnswers extends PrescreeningQuestionRecord {
  answers: {
    name: string;
    // A Checkbox answer is the list of ticked options, in the partner's order.
    answer: string | string[];
    type?: QuestionType;
    options?: string[];
  }[];
}

export type PrescreeningStatus =
  | "in_progress"
  | "submitted"
  | "pending_review"
  | "failed"
  | "abandoned"
  | null;

export type PrescreeningRecommendation = "accept" | "review" | "decline";

export type PrescreeningResult =
  | "failed"
  | "needs_review"
  | "abandoned"
  | "in_progress"
  | "submitted"
  | "passed"
  | "not_started";

export type PrescreeningRuleSetType = "review" | "decline";

export interface PrescreeningRuleCondition {
  met: boolean;
  skipped?: boolean;
  field: string;
  // Numeric criteria threshold is a number; range/yes-no is a string (or
  // string[] for multi-select range conditions).
  value: string | number | string[];
  operator: string;
  // Numeric criteria report a number; range/yes-no report a string; a Checkbox
  // question reports the list of ticked options.
  applicant_value: string | number | string[] | null;
  // Desired move-in date conditions: the date the days were counted from and how
  // many days after it the lead's date falls (CRM-1615).
  anchor_date?: string;
  days_after?: number;
  // Custom-question conditions name their question (CRM-1624); absent on older
  // results.
  question_id?: string;
  question_name?: string;
}

export interface PrescreeningRule {
  id: string;
  matched: boolean;
  conditions: PrescreeningRuleCondition[];
}

export interface PrescreeningRuleReason {
  rule_set_type: PrescreeningRuleSetType;
  rules: PrescreeningRule[];
}

export interface LeadPrescreeningsResponse {
  credit_score: PrescreeningCreditScore | null;
  income?: PrescreeningGrossMonthlyIncome;
  question_answers?: PrescreeningQuestionAnswers;
  status: PrescreeningStatus;
  submitted_at: string | null;
  abandoned_at: string | null;
  ip_address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  showing_scheduling_enabled?: boolean;
  identity_verification_enabled: boolean;
  identity_verification?: {
    status: string | null;
    finished: boolean;
  };
  recommendation?: PrescreeningRecommendation | null;
  rule_reasons?: PrescreeningRuleReason[];
  created_by_ai_leasing_agent: boolean;
}

export type LeadBulkActionKind = "assign" | "change_stage" | "delete";

export type LeadBulkActionStatus =
  | "pending"
  | "processing"
  | "completed"
  | "failed";

/** One filter-wide bulk run (CRM-1449), as returned by /portal/v1/leads/bulk_actions. */
export interface LeadBulkAction {
  id: number;
  action: LeadBulkActionKind;
  status: LeadBulkActionStatus;
  total_count: number | null;
  processed_count: number;
  failed_count: number;
  error: string | null;
  created_at: string;
  completed_at: string | null;
}

export const isLeadBulkActionSettled = (status: LeadBulkActionStatus) =>
  status === "completed" || status === "failed";

/**
 * The flat /portal/v1/leads filter vocabulary (see buildLeadsFilterPayload). Sent
 * top-level in the bulk_actions body so the API applies the exact same access and
 * entitlement filters it applies to the list.
 */
export type LeadsFilterPayload = Record<string, unknown>;

export interface CreateLeadBulkActionPayload {
  action: LeadBulkActionKind;
  stage_id?: number;
  assignee_ids?: number[];
  excluded_lead_ids: number[];
  filters: LeadsFilterPayload;
}

export type LeadsCsvExportSourcePage = "leads" | "property" | "unit";

export interface StartLeadsCsvExportPayload {
  filters: LeadsFilterPayload;
  order_by?: string;
  order_direction?: "asc" | "desc";
  columns: { id: string; label: string }[];
  timezone: string;
  source_page: LeadsCsvExportSourcePage;
  property_id?: string;
  unit_id?: string;
}

interface LeadsCsvExportStatus {
  status: "pending" | "done" | "failed";
  download_url?: string;
  filename?: string;
  row_count?: number;
  error?: string;
}

export const leadApi = {
  startCsvExport: async ({
    filters,
    ...rest
  }: StartLeadsCsvExportPayload): Promise<{ id: string; status: string }> => {
    const { data } = await instance.post<{ id: string; status: string }>(
      "/portal/v1/leads/export_csv",
      { ...filters, ...rest }
    );
    return data;
  },

  getCsvExportStatus: async (id: string): Promise<LeadsCsvExportStatus> => {
    const { data } = await instance.get<LeadsCsvExportStatus>(
      "/portal/v1/leads/export_csv_status",
      { params: { id } }
    );
    return data;
  },

  createBulkAction: async ({
    filters,
    ...rest
  }: CreateLeadBulkActionPayload): Promise<LeadBulkAction> => {
    const { data } = await instance.post<LeadBulkAction>(
      "/portal/v1/leads/bulk_actions",
      { ...filters, ...rest }
    );
    return data;
  },

  getBulkAction: async (id: number): Promise<LeadBulkAction> => {
    const { data } = await instance.get<LeadBulkAction>(
      `/portal/v1/leads/bulk_actions/${id}`
    );
    return data;
  },

  assignLead: async ({
    lead_id,
    assignee_ids
  }: {
    lead_id: number;
    assignee_ids: number[];
  }): Promise<Lead> => {
    const response = await instance.put<Lead>(`/portal/v1/leads/${lead_id}`, {
      assignee_ids
    });
    return response.data;
  },

  starLead: async ({
    lead_id,
    starred
  }: {
    lead_id: number;
    starred: boolean;
  }): Promise<Lead> => {
    const { data } = await instance.put<Lead>(
      `/portal/v1/leads/${lead_id}/star`,
      { starred }
    );
    return data;
  },

  getPrescreenings: async (
    leadId: string
  ): Promise<LeadPrescreeningsResponse> => {
    const { data } = await instance.get<LeadPrescreeningsResponse>(
      `/portal/v1/leads/${leadId}/prescreenings`
    );
    return data;
  },

  bulkAssignLeads: async ({
    lead_ids,
    assignee_ids
  }: {
    lead_ids: number[];
    assignee_ids: number[];
  }): Promise<Lead> => {
    return instance.post("/portal/v1/leads/bulk_assign", {
      lead_ids,
      assignee_ids
    });
  },

  bulkChangeStage: async ({
    lead_ids,
    stage_id
  }: {
    lead_ids: number[];
    stage_id: number;
  }): Promise<void> => {
    return instance.post("/portal/v1/leads/bulk_change_stage", {
      lead_ids,
      stage_id
    });
  }
};
